import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import { useCart, FREE_SHIPPING_FROM, SHIPPING_COST } from "@/context/CartContext";
import { useApi, euro, imgSrc, errMsg } from "@/lib/api";

const PAY_OPTIONS = [
  ["bizum", "Bizum", "Te enviamos las instrucciones al hacer el pedido"],
  ["transferencia", "Transferencia bancaria", "Te mostramos los datos de la cuenta al hacer el pedido"],
];

const PaymentChoice = ({ method, setMethod }) => (
  <div className="mt-6 space-y-2" data-testid="checkout-payment-methods">
    <p className="filter-title">Forma de pago</p>
    {PAY_OPTIONS.map(([v, label, hint]) => (
      <label key={v} className={`flex gap-3 p-3 border cursor-pointer transition-colors ${method === v ? "border-brand bg-slate-50" : "hover:border-slate-400"}`} data-testid={`checkout-pay-${v}`}>
        <input type="radio" name="pay" checked={method === v} onChange={() => setMethod(v)} className="mt-1 accent-[#0D1F7A]" />
        <span><span className="block text-sm font-semibold">{label}</span><span className="block text-xs text-slate-500">{hint}</span></span>
      </label>
    ))}
  </div>
);

const InvoiceFields = ({ form, setForm }) => (
  <div className="sm:col-span-2 border bg-slate-50 p-4" data-testid="checkout-invoice-box">
    <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
      <input type="checkbox" checked={form.invoice} onChange={(e) => setForm({ ...form, invoice: e.target.checked })} className="w-4 h-4 accent-[#1F8A3F]" data-testid="checkout-invoice-checkbox" />
      Quiero factura
    </label>
    {form.invoice && (
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">DNI / NIF / NIE</span>
          <input required value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="field mt-1 uppercase" placeholder="12345678Z" data-testid="checkout-tax-id-input" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Empresa (opcional)</span>
          <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="field mt-1" data-testid="checkout-company-input" />
        </label>
      </div>
    )}
  </div>
);

const CONTACT_FIELDS = [
  ["name", "Nombre completo", "sm:col-span-2"],
  ["email", "Email", ""],
  ["phone", "Teléfono", ""],
];
const ADDRESS_FIELDS = [
  ["address", "Dirección", "sm:col-span-2"],
  ["city", "Ciudad", ""],
  ["postal_code", "Código postal", ""],
];

const DELIVERY_OPTIONS = [
  ["envio", "Envío a domicilio", `24/48 h laborables · ${euro(SHIPPING_COST)} (gratis a partir de ${euro(FREE_SHIPPING_FROM)})`],
  ["recogida", "Recogida en tienda", "Gratis · C/ Granada, nº 4 - Local 3, Olula del Río. Te avisamos cuando esté listo"],
];

const DeliveryChoice = ({ delivery, setDelivery }) => (
  <div className="mb-8 space-y-2" data-testid="checkout-delivery-methods">
    <p className="filter-title">Entrega</p>
    {DELIVERY_OPTIONS.map(([v, label, hint]) => (
      <label key={v} className={`flex gap-3 p-3 border cursor-pointer transition-colors ${delivery === v ? "border-brand bg-slate-50" : "hover:border-slate-400"}`} data-testid={`checkout-delivery-${v}`}>
        <input type="radio" name="delivery" checked={delivery === v} onChange={() => setDelivery(v)} className="mt-1 accent-[#0D1F7A]" />
        <span><span className="block text-sm font-semibold">{label}</span><span className="block text-xs text-slate-500">{hint}</span></span>
      </label>
    ))}
  </div>
);

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const { user } = useUser();
  const call = useApi();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", postal_code: "", notes: "", invoice: false, tax_id: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [delivery, setDelivery] = useState("envio");
  const pickup = delivery === "recogida";
  const shipping = pickup || subtotal >= FREE_SHIPPING_FROM || subtotal === 0 ? 0 : SHIPPING_COST;
  const FIELDS = pickup ? CONTACT_FIELDS : [...CONTACT_FIELDS, ...ADDRESS_FIELDS];

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.fullName || "", email: f.email || user.primaryEmailAddress?.emailAddress || "" }));
  }, [user]);

  const [method, setMethod] = useState("bizum");
  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await call("post", "/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color || "", quantity: i.quantity })),
        customer: form,
        origin_url: window.location.origin,
        payment_method: method,
        delivery,
      });
      clear();
      navigate(`/pedido/${data.order_id}`);
    } catch (err) {
      toast.error(errMsg(err, "No se pudo hacer el pedido"));
      setBusy(false);
    }
  };

  if (!items.length) return (
    <div className="py-32 text-center" data-testid="checkout-empty">
      <p className="text-slate-500">Tu carrito está vacío.</p>
      <Link to="/tienda" className="btn-primary mt-6 inline-flex">Ir a la tienda</Link>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-14" data-testid="checkout-page">
      <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight">Finalizar compra</h1>
      <form onSubmit={pay} className="mt-10 grid lg:grid-cols-[1fr_400px] gap-12">
        <div>
          <Show when="signed-out">
            <div className="mb-8 p-4 bg-slate-50 border text-sm flex flex-wrap items-center justify-between gap-3" data-testid="checkout-guest-note">
              <span>Compras como invitado. ¿Quieres guardar tus pedidos?</span>
              <Link to="/sign-in" className="font-semibold underline" data-testid="checkout-signin-link">Inicia sesión</Link>
            </div>
          </Show>
          <DeliveryChoice delivery={delivery} setDelivery={setDelivery} />
          <p className="filter-title">{pickup ? "Tus datos" : "Datos de envío"}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map(([k, label, span]) => (
              <label key={k} className={`block ${span}`}>
                <span className="text-xs font-semibold text-slate-600">{label}{pickup && k === "phone" ? " (te avisamos cuando esté listo)" : ""}{pickup && k === "email" ? " (opcional, para enviarte copia del pedido)" : ""}</span>
                <input required={pickup ? k !== "email" : k !== "phone"} type={k === "email" ? "email" : "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="field mt-1" data-testid={`checkout-${k}-input`} />
              </label>
            ))}
            <InvoiceFields form={form} setForm={setForm} />
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold text-slate-600">Notas (opcional)</span>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="field mt-1 h-20 py-2" data-testid="checkout-notes-input" />
            </label>
          </div>
        </div>
        <aside className="bg-white border p-6 h-fit lg:sticky lg:top-28" data-testid="checkout-summary">
          <p className="filter-title">Resumen</p>
          <ul className="divide-y">
            {items.map((i) => (
              <li key={i.key} className="py-3 flex gap-3 text-sm">
                <img src={imgSrc(i.image)} alt="" className="w-12 h-14 object-cover bg-slate-100" />
                <div className="flex-1"><p className="font-semibold">{i.name}</p><p className="text-xs text-slate-500">Talla {i.size}{i.color && ` · ${i.color}`} · x{i.quantity}</p></div>
                <span className="font-semibold">{euro(i.price * i.quantity)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 space-y-2 text-sm border-t pt-4">
            <div className="flex justify-between"><span>Subtotal</span><span>{euro(subtotal)}</span></div>
            <div className="flex justify-between"><span data-testid="checkout-shipping-label">{pickup ? "Recogida en tienda" : "Envío"}</span><span data-testid="checkout-shipping">{shipping ? euro(shipping) : "Gratis"}</span></div>
            <div className="flex justify-between text-lg font-bold pt-2"><span>Total</span><span data-testid="checkout-total">{euro(subtotal + shipping)}</span></div>
            <p className="text-xs text-slate-500">IVA incluido · <Link to="/devoluciones" className="underline" data-testid="checkout-returns-link">Política de devoluciones</Link></p>
            <p className="text-xs text-slate-500" data-testid="checkout-legal-note">Al hacer el pedido aceptas el <Link to="/legal/aviso-legal" className="underline">aviso legal</Link> y la <Link to="/legal/privacidad" className="underline">política de privacidad</Link>.</p>
          </div>
          <PaymentChoice method={method} setMethod={setMethod} />
          <button disabled={busy} className="btn-primary w-full mt-6" data-testid="checkout-pay-button">
            <ShoppingBag className="w-4 h-4" /> {busy ? "Enviando pedido…" : "Hacer pedido"}
          </button>
        </aside>
      </form>
    </div>
  );
}
